// Marketing Hub - Dashboard Acquisition P0
// CMO IA - Mode LECTURE uniquement (observation, analyse, alertes)

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  TrendingUp, TrendingDown, Users, Target, Search, Globe,
  AlertTriangle, CheckCircle, Clock, RefreshCw, Loader2,
  BarChart3, PieChart, Activity, ExternalLink, Filter,
  Building2, Briefcase, MousePointer, Eye, ArrowUpRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = "emerald" }) => {
  const colorClasses = {
    emerald: "from-emerald-500/20 to-emerald-600/10 text-emerald-400",
    blue: "from-blue-500/20 to-blue-600/10 text-blue-400",
    amber: "from-amber-500/20 to-amber-600/10 text-amber-400",
    purple: "from-purple-500/20 to-purple-600/10 text-purple-400",
    rose: "from-rose-500/20 to-rose-600/10 text-rose-400"
  };
  
  return (
    <Card className="bg-zinc-900/50 border-white/10 overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2">
            {trend >= 0 ? (
              <TrendingUp className="w-3 h-3 text-emerald-400" />
            ) : (
              <TrendingDown className="w-3 h-3 text-rose-400" />
            )}
            <span className={`text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
            <span className="text-xs text-zinc-500">vs période précédente</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Alert Card Component
const AlertCard = ({ alert }) => {
  const severityColors = {
    high: "border-rose-500/50 bg-rose-500/5",
    medium: "border-amber-500/50 bg-amber-500/5",
    low: "border-blue-500/50 bg-blue-500/5"
  };
  
  const typeIcons = {
    alert: AlertTriangle,
    warning: AlertTriangle,
    info: CheckCircle
  };
  
  const Icon = typeIcons[alert.type] || AlertTriangle;
  
  return (
    <div className={`p-3 rounded-lg border ${severityColors[alert.severity] || severityColors.medium}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-4 h-4 mt-0.5 ${alert.severity === 'high' ? 'text-rose-400' : 'text-amber-400'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white">{alert.message}</p>
          {alert.action_suggested && (
            <p className="text-xs text-zinc-400 mt-1">💡 {alert.action_suggested}</p>
          )}
          {alert.activite_code && (
            <Badge className="mt-2 bg-zinc-800 text-zinc-400 border-0 text-xs">
              {alert.activite_code}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

// Source Badge Component
const SourceBadge = ({ source, count }) => {
  const sourceConfig = {
    site_web: { label: "Site Web", icon: Globe, color: "bg-blue-500/20 text-blue-400" },
    landing_page: { label: "Landing Page", icon: Target, color: "bg-purple-500/20 text-purple-400" },
    facebook: { label: "Facebook", icon: Users, color: "bg-blue-600/20 text-blue-300" },
    linkedin: { label: "LinkedIn", icon: Briefcase, color: "bg-sky-500/20 text-sky-400" },
    email: { label: "Email", icon: Activity, color: "bg-amber-500/20 text-amber-400" },
    formulaire: { label: "Formulaire", icon: Target, color: "bg-emerald-500/20 text-emerald-400" },
    autre: { label: "Autre", icon: Activity, color: "bg-zinc-500/20 text-zinc-400" }
  };
  
  const config = sourceConfig[source] || sourceConfig.autre;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.color}`}>
      <Icon className="w-4 h-4" />
      <span className="text-sm">{config.label}</span>
      <span className="font-bold">{count}</span>
    </div>
  );
};

// Activity Bar (mini chart)
const ActivityBar = ({ data, maxValue }) => {
  const height = maxValue > 0 ? (data.leads / maxValue) * 100 : 0;
  
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-8 h-16 bg-zinc-800 rounded overflow-hidden flex items-end">
        <div 
          className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t transition-all duration-300"
          style={{ height: `${height}%` }}
        />
      </div>
      <span className="text-xs text-zinc-500">{data.jour}</span>
      <span className="text-xs text-zinc-400">{data.leads}</span>
    </div>
  );
};

export default function DashboardAcquisition() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedActivite, setSelectedActivite] = useState(null);
  const [activites, setActivites] = useState([]);
  
  const loadDashboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (selectedActivite) params.append("activite_id", selectedActivite);
      
      const [dashboardRes, activitesRes] = await Promise.all([
        axios.get(`${API}/api/marketing/config/dashboard/acquisition?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/marketing/config/activites`, { headers: getAuthHeaders() })
      ]);
      
      setData(dashboardRes.data);
      setActivites(activitesRes.data);
    } catch (err) {
      console.error("Erreur chargement dashboard:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedActivite]);
  
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }
  
  const maxLeadsDay = data?.evolution_7j ? Math.max(...data.evolution_7j.map(d => d.leads), 1) : 1;
  
  return (
    <div className="space-y-6" data-testid="dashboard-acquisition">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-emerald-400" />
            Dashboard Acquisition
          </h1>
          <p className="text-zinc-400">CMO IA • Mode Observation • Données temps réel</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filtre Activité */}
          <select
            value={selectedActivite || ""}
            onChange={(e) => setSelectedActivite(e.target.value || null)}
            className="h-9 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-white text-sm"
          >
            <option value="">Toutes activités</option>
            {activites.map(act => (
              <option key={act.id} value={act.id}>{act.code} - {act.nom}</option>
            ))}
          </select>
          
          <Button 
            variant="outline" 
            onClick={() => loadDashboard(true)}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
      
      {/* Alertes (si présentes) */}
      {data?.alertes && data.alertes.length > 0 && (
        <Card className="bg-zinc-900/50 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Alertes ({data.alertes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.alertes.slice(0, 3).map((alert, idx) => (
              <AlertCard key={idx} alert={alert} />
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* KPIs Principaux */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          title="Leads Total"
          value={data?.leads?.total || 0}
          subtitle={`${data?.leads?.periode || 0} sur la période`}
          icon={Users}
          color="emerald"
        />
        <KPICard
          title="Nouveaux"
          value={data?.leads?.nouveaux || 0}
          subtitle="À qualifier"
          icon={Target}
          color="blue"
        />
        <KPICard
          title="Qualifiés"
          value={data?.leads?.qualifies || 0}
          subtitle={`${data?.leads?.contactes || 0} contactés`}
          icon={CheckCircle}
          color="amber"
        />
        <KPICard
          title="Taux Conversion"
          value={`${data?.leads?.taux_conversion || 0}%`}
          subtitle={`${data?.leads?.convertis || 0} convertis`}
          icon={TrendingUp}
          color="purple"
        />
      </div>
      
      {/* Section SEO */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-400" />
              SEO Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400 text-sm">Mots-clés suivis</span>
                <span className="text-white font-bold">{data?.seo?.keywords_suivis || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400 text-sm">Position moyenne</span>
                <span className="text-white font-bold">
                  {data?.seo?.position_moyenne || "—"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400 text-sm">Top 10</span>
                <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                  {data?.seo?.keywords_top10 || 0}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400 text-sm">Top 30</span>
                <Badge className="bg-blue-500/20 text-blue-400 border-0">
                  {data?.seo?.keywords_top30 || 0}
                </Badge>
              </div>
            </div>
            
            {/* Indicateur connexion GSC */}
            <div className="mt-4 pt-3 border-t border-white/5">
              <div className="flex items-center gap-2 text-xs">
                {data?.seo?.keywords_suivis > 0 ? (
                  <>
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-zinc-400">Google Search Console connecté</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 rounded-full bg-zinc-600" />
                    <span className="text-zinc-500">GSC non configuré</span>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Répartition par Source */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <PieChart className="w-4 h-4 text-purple-400" />
              Sources d'acquisition
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data?.par_source || {}).length === 0 ? (
              <p className="text-zinc-500 text-sm italic">Aucune donnée source</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(data.par_source).map(([source, count]) => (
                  <SourceBadge key={source} source={source} count={count} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Répartition par Activité */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-amber-400" />
              Par activité
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data?.par_activite || {}).length === 0 ? (
              <p className="text-zinc-500 text-sm italic">Aucune donnée activité</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(data.par_activite).map(([code, info]) => (
                  <div key={code} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: info.couleur || 'var(--oh-stat-blue-text)' }}
                      />
                      <span className="text-sm text-zinc-300">{code}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold">{info.count}</span>
                      <span className="text-xs text-zinc-500">leads</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Évolution 7 jours */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" />
            Évolution des leads (7 derniers jours)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.evolution_7j && data.evolution_7j.length > 0 ? (
            <div className="flex items-end justify-around">
              {data.evolution_7j.map((day, idx) => (
                <ActivityBar key={idx} data={day} maxValue={maxLeadsDay} />
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm italic text-center py-8">
              Aucune donnée d'évolution disponible
            </p>
          )}
        </CardContent>
      </Card>
      
      {/* Footer Info */}
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span>Dernière mise à jour : {data?.generated_at ? new Date(data.generated_at).toLocaleString('fr-FR') : '—'}</span>
        <span>Période : {data?.periode || '30j'}</span>
      </div>
    </div>
  );
}
